import React, { useState } from 'react';
import {
  AutoComplete,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  MinusCircleOutlined,
  PlusOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import {
  PartnerDto,
  PartnerSocialLink,
  PartnerTier,
  useCreatePartner,
  useDeletePartner,
  usePartners,
  useUpdatePartner,
} from '@shared/api/hooks/usePartners';
import { MediaOrUrlField } from '@shared/components/MediaOrUrlField';

const TIER_LABELS: Record<PartnerTier, string> = {
  MAIN: 'Main',
  GOLD: 'Gold',
  SILVER: 'Silver',
  MEDIA: 'Media',
  PREMIUM: 'Premium',
  TECHNICAL: 'Technical',
  OFFICIAL: 'Official',
};

const TIER_COLORS: Record<PartnerTier, string> = {
  MAIN: 'red',
  GOLD: 'orange',
  SILVER: '#8c8c8c',
  MEDIA: 'purple',
  PREMIUM: 'gold',
  TECHNICAL: 'blue',
  OFFICIAL: 'default',
};

// Piattaforme note: rese con logo lato mobile. Il campo resta libero (l'admin
// può digitare una chiave extra), il mobile usa un'icona link generica per le
// chiavi fuori da questo set. Il "Sito web" resta un campo a parte (websiteUrl).
const KNOWN_PLATFORMS = ['instagram', 'facebook', 'youtube', 'tiktok', 'x', 'linkedin'] as const;

interface FormValues {
  name: string;
  descIt?: string;
  descEn?: string;
  logoUrl: string;
  websiteUrl?: string;
  socialLinks?: PartnerSocialLink[];
  tier: PartnerTier;
  position: number;
  isActive: boolean;
}

export function PartnersPage(): React.ReactElement {
  const [filterTier, setFilterTier] = useState<PartnerTier | undefined>(undefined);
  const [filterActive, setFilterActive] = useState<boolean>(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form] = Form.useForm<FormValues>();

  const { data: partners = [], isLoading, refetch, isFetching } = usePartners({
    tier: filterTier,
    active: filterActive,
  });
  const createMutation = useCreatePartner();
  const updateMutation = useUpdatePartner();
  const deleteMutation = useDeletePartner();

  const openCreate = (): void => {
    setEditId(null);
    form.resetFields();
    form.setFieldsValue({ tier: 'OFFICIAL', position: partners.length, isActive: true });
    setModalOpen(true);
  };

  const openEdit = (p: PartnerDto): void => {
    setEditId(p.id);
    const d = (p.description ?? {}) as { it?: string; en?: string };
    form.setFieldsValue({
      name: p.name,
      descIt: d.it,
      descEn: d.en,
      logoUrl: p.logoUrl,
      websiteUrl: p.websiteUrl ?? undefined,
      socialLinks: p.socialLinks ?? [],
      tier: p.tier,
      position: p.position,
      isActive: p.isActive,
    });
    setModalOpen(true);
  };

  const onSubmit = async (): Promise<void> => {
    const v = await form.validateFields();
    const description =
      v.descIt || v.descEn
        ? { ...(v.descIt ? { it: v.descIt } : {}), ...(v.descEn ? { en: v.descEn } : {}) }
        : undefined;
    // Scarta le righe social vuote/parziali e normalizza la platform.
    const socialLinks = (v.socialLinks ?? [])
      .map((s) => ({ platform: (s?.platform ?? '').trim().toLowerCase(), url: (s?.url ?? '').trim() }))
      .filter((s) => s.platform !== '' && s.url !== '');
    const input = {
      name: v.name,
      description,
      logoUrl: v.logoUrl,
      websiteUrl: v.websiteUrl || undefined,
      socialLinks,
      tier: v.tier,
      position: Number(v.position),
      isActive: v.isActive,
    };
    try {
      if (editId) {
        await updateMutation.mutateAsync({ id: editId, input });
        message.success('Partner aggiornato');
      } else {
        await createMutation.mutateAsync(input);
        message.success('Partner creato');
      }
      setModalOpen(false);
    } catch (e) {
      const err = e as { response?: { data?: { message?: string | string[] } } };
      const msg = err.response?.data?.message;
      message.error(Array.isArray(msg) ? msg.join('; ') : (msg ?? 'Errore'));
    }
  };

  const onDelete = async (id: string): Promise<void> => {
    try {
      await deleteMutation.mutateAsync(id);
      message.success('Partner eliminato');
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } } };
      message.error(err.response?.data?.message ?? 'Errore');
    }
  };

  const onMove = async (p: PartnerDto, delta: number): Promise<void> => {
    try {
      await updateMutation.mutateAsync({
        id: p.id,
        input: { position: Math.max(0, p.position + delta) },
      });
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } } };
      message.error(err.response?.data?.message ?? 'Errore');
    }
  };

  return (
    <Card
      title={
        <Space>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Partner
          </Typography.Title>
          <Tag>{partners.length}</Tag>
        </Space>
      }
      extra={
        <Space>
          <Select
            placeholder="Tier"
            style={{ width: 140 }}
            allowClear
            value={filterTier}
            onChange={(v) => setFilterTier(v as PartnerTier | undefined)}
            options={(Object.keys(TIER_LABELS) as PartnerTier[]).map((t) => ({
              value: t,
              label: TIER_LABELS[t],
            }))}
          />
          <Space>
            <Typography.Text type="secondary">Attivi</Typography.Text>
            <Switch checked={filterActive} onChange={setFilterActive} />
          </Space>
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching}>
            Aggiorna
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Nuovo partner
          </Button>
        </Space>
      }
    >
      <Table<PartnerDto>
        rowKey="id"
        loading={isLoading}
        dataSource={partners}
        pagination={false}
        scroll={{ x: 'max-content' }}
        columns={[
          {
            title: '#',
            dataIndex: 'position',
            width: 80,
            align: 'center',
            render: (v: number, r) => (
              <Space size={2}>
                <Button size="small" type="text" onClick={() => onMove(r, -1)}>
                  ↑
                </Button>
                <Typography.Text>{v}</Typography.Text>
                <Button size="small" type="text" onClick={() => onMove(r, 1)}>
                  ↓
                </Button>
              </Space>
            ),
          },
          {
            title: 'Logo',
            dataIndex: 'logoUrl',
            width: 80,
            render: (v: string) => (
              <img
                src={v}
                alt=""
                width={56}
                height={32}
                style={{ objectFit: 'contain' }}
              />
            ),
          },
          {
            title: 'Nome',
            render: (_v, r) => (
              <Space direction="vertical" size={0}>
                <Typography.Text strong>{r.name}</Typography.Text>
                {r.websiteUrl ? (
                  <Typography.Link href={r.websiteUrl} target="_blank" style={{ fontSize: 12 }}>
                    {r.websiteUrl}
                  </Typography.Link>
                ) : null}
              </Space>
            ),
          },
          {
            title: 'Tier',
            dataIndex: 'tier',
            width: 130,
            render: (v: PartnerTier) => <Tag color={TIER_COLORS[v]}>{TIER_LABELS[v]}</Tag>,
          },
          {
            title: 'Stato',
            dataIndex: 'isActive',
            width: 100,
            render: (v: boolean) =>
              v ? <Tag color="green">Attivo</Tag> : <Tag color="default">Inattivo</Tag>,
          },
          {
            title: '',
            width: 100,
            align: 'right',
            render: (_v, r) => (
              <Space>
                <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
                <Popconfirm
                  title="Eliminare partner?"
                  okText="Elimina"
                  cancelText="Annulla"
                  okButtonProps={{ danger: true }}
                  onConfirm={() => onDelete(r.id)}
                >
                  <Button size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />

      <Modal
        title={editId ? 'Modifica partner' : 'Nuovo partner'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={onSubmit}
        okText={editId ? 'Salva' : 'Crea'}
        confirmLoading={createMutation.isPending || updateMutation.isPending}
        destroyOnHidden
        width={620}
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item
            name="name"
            label="Nome partner"
            rules={[{ required: true, max: 255 }]}
          >
            <Input placeholder="Banca Tercas" />
          </Form.Item>
          <Form.Item
            name="logoUrl"
            label="Logo"
            rules={[{ required: true, message: 'Seleziona un logo dalla libreria media' }]}
          >
            <MediaOrUrlField category="PARTNER_LOGO" placeholder="https://..." />
          </Form.Item>
          <Form.Item name="websiteUrl" label="Sito web">
            <Input placeholder="https://..." />
          </Form.Item>
          <Form.Item label="Social">
            <Form.List name="socialLinks">
              {(fields, { add, remove }) => (
                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                  {fields.map((field) => (
                    <Space.Compact key={field.key} style={{ width: '100%' }}>
                      <Form.Item
                        name={[field.name, 'platform']}
                        rules={[{ required: true, message: 'Piattaforma' }]}
                        style={{ width: 170, marginBottom: 0 }}
                      >
                        <AutoComplete
                          placeholder="Piattaforma"
                          options={KNOWN_PLATFORMS.map((p) => ({ value: p }))}
                          filterOption={(input, opt) =>
                            (opt?.value ?? '').toString().toLowerCase().includes(input.toLowerCase())
                          }
                        />
                      </Form.Item>
                      <Form.Item
                        name={[field.name, 'url']}
                        rules={[
                          { required: true, message: 'URL' },
                          { type: 'url', message: 'URL http/https non valido' },
                        ]}
                        style={{ flex: 1, marginBottom: 0 }}
                      >
                        <Input placeholder="https://instagram.com/..." />
                      </Form.Item>
                      <Button
                        type="text"
                        danger
                        icon={<MinusCircleOutlined />}
                        onClick={() => remove(field.name)}
                      />
                    </Space.Compact>
                  ))}
                  <Button
                    type="dashed"
                    onClick={() => add({ platform: undefined, url: '' })}
                    icon={<PlusOutlined />}
                    block
                  >
                    Aggiungi social
                  </Button>
                </Space>
              )}
            </Form.List>
          </Form.Item>
          <Space.Compact style={{ width: '100%' }}>
            <Form.Item
              name="tier"
              label="Tier"
              rules={[{ required: true }]}
              style={{ flex: 1, marginRight: 8 }}
            >
              <Select
                options={(Object.keys(TIER_LABELS) as PartnerTier[]).map((t) => ({
                  value: t,
                  label: TIER_LABELS[t],
                }))}
              />
            </Form.Item>
            <Form.Item
              name="position"
              label="Posizione"
              rules={[{ required: true, type: 'number', min: 0 }]}
              style={{ width: 130, marginRight: 8 }}
            >
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="isActive" label="Attivo" valuePropName="checked" style={{ width: 100 }}>
              <Switch />
            </Form.Item>
          </Space.Compact>
          <Form.Item name="descIt" label="Descrizione (IT)">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="descEn" label="Descrizione (EN)">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
